import { Box } from '@mui/material';
import styled from 'styled-components';
import { LinkText } from 'shared/styles';
import useAppNavigation from 'hooks/useAppNavigation';
import { DataCellProps } from '../types';
import { MemberSearchItem } from '../types';
import EllipsisText from 'components/EllipsisText';

const NameContainer = styled.div`
  max-width: 250px;
  min-width: 0;
`;

const EmailContainer = styled.div`
  max-width: 300px;
  min-width: 0;
`;

type MemberDisplayableKey = keyof Pick<
  MemberSearchItem,
  'tenant' | 'realmId' | 'teamId' | 'email' | 'lastBackfilled' | 'id'
>;

const isMemberDisplayableKey = (key: string): key is MemberDisplayableKey => {
  return (
    key === 'tenant' ||
    key === 'realmId' ||
    key === 'teamId' ||
    key === 'email' ||
    key === 'lastBackfilled' ||
    key === 'id'
  );
};

export const MembersV2TextCell = ({ member, column }: DataCellProps) => {
  const { navigateToTeamProfile } = useAppNavigation();
  const colId = typeof column === 'object' ? column.id : column;
  if (colId === 'name') {
    // API returns camelCase: firstName, lastName
    const displayValue =
      [member.firstName, member.lastName].filter(Boolean).join(' ').trim() || '\u2014';

    return (
      <NameContainer>
        <EllipsisText text={displayValue}>{displayValue}</EllipsisText>
      </NameContainer>
    );
  }
  if (colId === 'teamId') {
    const navigateToTeam = () => {
      const idNum = parseInt(String(member.teamId), 10);
      if (Number.isNaN(idNum)) return;

      // Open in new tab - useEnsureTeamContext on TeamProfilePage handles tenant switching
      navigateToTeamProfile({
        teamId: idNum,
        tenant: member.tenant,
        realmId: member.realmId,
        shouldOpenInAnotherTab: true,
      });
    };
    const handleClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
      e.stopPropagation();
      navigateToTeam();
    };
    const handleKeyDown: React.KeyboardEventHandler<HTMLSpanElement> = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        navigateToTeam();
      }
    };
    return (
      <LinkText role="link" tabIndex={0} onClick={handleClick} onKeyDown={handleKeyDown}>
        {member.teamId}
      </LinkText>
    );
  }
  const value = isMemberDisplayableKey(colId) ? member[colId] : undefined;

  // Apply ellipsis for email column
  if (colId === 'email') {
    return (
      <EmailContainer>
        <EllipsisText text={String(value || '')}>{value ?? ''}</EllipsisText>
      </EmailContainer>
    );
  }

  return <Box>{value ?? ''}</Box>;
};

export default MembersV2TextCell;
